import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { z } from 'zod';

import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Alert from '../../components/ui/Alert';
import { useAuth } from '../../contexts/AuthContext';

import { ROTAS } from '@/routes/routePaths';

// Esquema de validação para o formulário de registro
const registerSchema = z.object({
  nome: z.string().min(3, 'Nome deve ter pelo menos 3 caracteres.').max(255, 'Nome muito longo.'),
  telefone: z.string().optional().nullable().refine(
    (val) => !val || /^\(\d{2}\)\s\d{4,5}-\d{4}$/.test(val),
    'Telefone inválido. Use o formato (XX) XXXXX-XXXX ou (XX) XXXX-XXXX.'
  ),
  email: z.string().email('Email inválido.').max(255, 'Email muito longo.'),
  senha: z.string().min(6, 'A senha deve ter pelo menos 6 caracteres.'),
  confirmacao_senha: z.string().min(6, 'A confirmação de senha deve ter pelo menos 6 caracteres.'),
  aceitou_termos: z.boolean().refine((val) => val === true, {
    message: 'Você deve aceitar os termos e condições para se registrar.',
  }),
}).refine((data) => data.senha === data.confirmacao_senha, {
  message: 'As senhas não coincidem.',
  path: ['confirmacao_senha'],
});

type RegisterFormData = z.infer<typeof registerSchema>;

const RegisterPage: React.FC = () => {
  const { cadastro } = useAuth();
  const navigate = useNavigate();
  const [erroMensagem, setErroMensagem] = useState<string | null>(null);
  const [sucessoMensagem, setSucessoMensagem] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const usuario = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterFormData) => {
    setCarregando(true);
    setErroMensagem(null);
    setSucessoMensagem(null);
    try {
      // ✅ CORREÇÃO: Removidos os parâmetros desnecessários
      await cadastro(data.email, data.senha, data.nome, '' + data.telefone, 'cliente');
      
      setSucessoMensagem('Registro realizado com sucesso! Verifique seu email para validar sua conta.');

    } catch (error: any) {
      console.error('Erro ao registrar:', error);
      setErroMensagem(error.message || 'Ocorreu um erro ao tentar registrar. Tente novamente.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (usuario.usuario) {
      navigate('/');
    }
  }, [usuario, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background principal da página - FIXADO NO FUNDO */}
      <div className="fixed inset-0 z-0">
        <img
          src="/Background-secao2.png"
          alt="Background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20"></div>
      </div>

      {/* Card de registro */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 bg-background/95 backdrop-blur-sm p-8 rounded-lg shadow-2xl max-w-md w-full border border-primary overflow-hidden"
      >
        {/* Background interno do card */}
        <div className="absolute inset-0 z-0">
          <img
            src="/BG.png"
            alt="Background do card"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-black/5"></div>
        </div>

        {/* Conteúdo do card */}
        <div className="relative z-20 -mt-10">
          <div className="flex justify-center mb-6">
            <Link to={'/'}>
              <img
                src={'/clube.png'}
                alt="TugasCoins Logo"
                className='h-64 object-contain'
              />
            </Link>
          </div>

          <h2 className="text-3xl font-bold -mt-20 text-center text-primary mb-4">
            Crie sua Conta
          </h2>
          <p className="text-center text-text-light mb-6">
            Junte-se ao Clube Mr. Tugas e comece<br /> a ganhar cashback!
          </p>

          {erroMensagem && (
            <Alert
              tipo="error"
              mensagem={erroMensagem}
              onClose={() => setErroMensagem(null)}
              className="mb-4"
            />
          )}
          {sucessoMensagem && (
            <Alert
              tipo="success"
              mensagem={sucessoMensagem}
              onClose={() => setSucessoMensagem(null)}
              className="mb-4"
            />
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="nome" className="block text-text text-sm font-medium mb-2">
                Nome Completo
              </label>
              <Input
                id="nome"
                tipo="text"
                placeholder="Seu nome completo"
                {...register('nome')}
                className="w-full"
              />
              {errors.nome && <p className="text-danger text-sm mt-1">{errors.nome.message}</p>}
            </div>

            <div>
              <label htmlFor="email" className="block text-text text-sm font-medium mb-2">
                Email
              </label>
              <Input
                id="email"
                tipo="email"
                placeholder="Seu melhor email"
                {...register('email')}
                className="w-full"
              />
              {errors.email && <p className="text-danger text-sm mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label htmlFor="telefone" className="block text-text text-sm font-medium mb-2">
                Telefone
              </label>
              <Input
                id="telefone"
                tipo="tel"
                placeholder="(XX) XXXXX-XXXX"
                {...register('telefone')}
                className="w-full"
                onInput={(e) => {
                  // Remove tudo que não é dígito
                  const value = e.currentTarget.value.replace(/\D/g, '');

                  // Aplica a formatação
                  if (value.length <= 2) {
                    e.currentTarget.value = value;
                  } else if (value.length <= 6) {
                    e.currentTarget.value = `(${value.slice(0, 2)}) ${value.slice(2)}`;
                  } else if (value.length <= 10) {
                    e.currentTarget.value = `(${value.slice(0, 2)}) ${value.slice(2, 6)}-${value.slice(6)}`;
                  } else {
                    e.currentTarget.value = `(${value.slice(0, 2)}) ${value.slice(2, 7)}-${value.slice(7, 11)}`;
                  }
                }}
                maxLength={15}
              />
              {errors.telefone && <p className="text-danger text-sm mt-1">{errors.telefone.message}</p>}
            </div>

            <div>
              <label htmlFor="senha" className="block text-text text-sm font-medium mb-2">
                Senha
              </label>
              <Input
                id="senha"
                tipo="password"
                placeholder="Crie uma senha"
                {...register('senha')}
                className="w-full"
              />
              {errors.senha && <p className="text-danger text-sm mt-1">{errors.senha.message}</p>}
            </div>

            <div>
              <label htmlFor="confirmacao_senha" className="block text-text text-sm font-medium mb-2">
                Confirmar Senha
              </label>
              <Input
                id="confirmacao_senha"
                tipo="password"
                placeholder="Confirme sua senha"
                {...register('confirmacao_senha')}
                className="w-full"
              />
              {errors.confirmacao_senha && <p className="text-danger text-sm mt-1">{errors.confirmacao_senha.message}</p>}
            </div>

            <div className="flex items-start space-x-2 mt-4">
              <input
                id="aceitou_termos"
                type="checkbox"
                {...register('aceitou_termos')}
                className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
              />
              <label htmlFor="aceitou_termos" className="text-sm text-text-light">
                Eu concordo com os{' '}
                <Link
                  to="/termos-condicoes"
                  className="text-primary hover:text-accent font-medium underline"
                  target="_blank"
                >
                  Termos e Condições
                </Link>{' '}
                e a{' '}
                <Link
                  to="/politica-privacidade"
                  className="text-primary hover:text-accent font-medium underline"
                  target="_blank"
                >
                  Política de Privacidade
                </Link>
              </label>
            </div>
            {errors.aceitou_termos && (
              <p className="text-danger text-sm mt-1">{errors.aceitou_termos.message}</p>
            )}
            <Button
              tipo="primary"
              type="submit"
              className="w-full py-3 text-lg font-semibold mt-4 hover:bg-gradient-to-r hover:from-[#c5a663] hover:via-[#cebc6e] hover:to-[#c5a663]"
              disabled={carregando}
            >
              {carregando ? 'Registrando...' : 'Registrar'}
            </Button>
          </form>

          <p className="text-center text-text-light mt-6">
            Já tem uma conta?{' '}
            <Link to={ROTAS.LOGIN} className="text-primary hover:text-accent font-medium transition-colors duration-200">
              Faça Login
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default RegisterPage;